import { format, formatDuration, parse, parseISO } from 'date-fns';

type FormatCurrencyType = {
  value: number | null;
  nullValue?: string;
  currencyCode: string;
};

const currencyFormatterUSD = new Intl.NumberFormat('en-us', {
  style: 'currency',
  currency: 'USD',
});

const currencyFormatterGBP = new Intl.NumberFormat('en-gb', {
  style: 'currency',
  currency: 'GBP',
});

const compactCurrencyFormatterUSD = new Intl.NumberFormat('en-us', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 0,
});

const compactCurrencyFormatterGBP = new Intl.NumberFormat('en-gb', {
  style: 'currency',
  currency: 'GBP',
  notation: 'compact',
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat('en-us', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const decimalFormatterTwo = new Intl.NumberFormat('en-us', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const decimalFormatterStrict = new Intl.NumberFormat('en-us', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const decimalFormatterTwoStrict = new Intl.NumberFormat('en-us', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('en-us', {
  maximumFractionDigits: 0,
});

const percentFormatter = new Intl.NumberFormat('en-us', {
  style: 'percent',
  maximumFractionDigits: 0,
});

const ensureDate = (dateOrStr: string | Date): Date => {
  if (dateOrStr instanceof Date) {
    return dateOrStr;
  }

  // Handle ISO strings (with time component)
  if (dateOrStr.includes('T') || dateOrStr.includes('Z')) {
    return parseISO(dateOrStr);
  }

  // Handle date-only strings in yyyy-MM-dd format
  return parse(dateOrStr, 'yyyy-MM-dd', new Date());
};

export const toDateLocal = (date: string | Date | null | undefined): string => {
  if (date == null || date === '') return '';
  return format(ensureDate(date), `yyyy-MM-dd`);
};

export const toDateFormatMMddyyyy = (date: string | Date | null | undefined): string => {
  if (date == null || date === '') return '';
  return format(ensureDate(date), `MM/dd/yyyy`);
};

export const toLongDateFormat = (dateOrStr: string | Date | null | undefined): string => {
  if (dateOrStr == null || dateOrStr === '') return '';
  const date = ensureDate(dateOrStr);
  return format(date, `MMM do yyyy`);
};

export const formatCurrency = ({
  value,
  nullValue = '-',
  currencyCode,
}: FormatCurrencyType): string => {
  return value === null
    ? nullValue
    : currencyCode === 'USD'
      ? currencyFormatterUSD.format(value)
      : currencyFormatterGBP.format(value);
};

// Compact currency for glanceable figures, e.g. 312000 -> "$312K", 640 -> "$640".
export const formatCompactCurrency = (
  value: number | null,
  currencyCode: string,
  nullValue = '-',
): string => {
  return value === null
    ? nullValue
    : currencyCode === 'USD'
      ? compactCurrencyFormatterUSD.format(value)
      : compactCurrencyFormatterGBP.format(value);
};

export const formatDecimal = (value: number | null, nullValue = '-'): string => {
  return value === null ? nullValue : decimalFormatter.format(value);
};

export const formatDecimalTwo = (value: number | null, nullValue = '-'): string => {
  return value === null ? nullValue : decimalFormatterTwo.format(value);
};

export const formatDecimalStrict = (value: number | null, nullValue = '-'): string => {
  return value === null ? nullValue : decimalFormatterStrict.format(value);
};

export const formatDecimalTwoStrict = (value: number | null, nullValue = '-'): string => {
  return value === null ? nullValue : decimalFormatterTwoStrict.format(value);
};

export const formatNumber = (value: number | null, nullValue = '-'): string => {
  return value === null ? nullValue : numberFormatter.format(value);
};

export const formatPercent = (value: number | null, nullValue = '-'): string => {
  return value === null ? nullValue : percentFormatter.format(value);
};

export const formatSecondstoHours = (value: number | null, nullValue = '-'): string => {
  return value === null || value === 0
    ? nullValue
    : formatDuration(
        {
          hours: Math.round(value / 3600),
        },
        {
          format: ['hours'],
        },
      );
};

export const formatSecondsToDays = (value: number | null, nullValue = '-'): string => {
  return value === null || value === 0
    ? nullValue
    : formatDuration(
        {
          days: Math.round(value / 60 / 60 / 24),
        },
        {
          format: ['days'],
        },
      );
};
